import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdioma } from '../idioma';
import { useTema } from '../tema';

/**
 * Cloudflare Turnstile (08-10-2026): Supabase pide un token de «eres una persona» para mandar el enlace, entrar con
 * contraseña y recuperarla (captcha nativo de Auth). Esta es la clave pública del widget «IGOR» (iggy. e igor.);
 * la secreta solo vive en Supabase. Google no lo necesita: la verificación la hace Google.
 */
const CLAVE_DEL_SITIO = '0x4AAAAAAFRyLcILRdynRlfz';
const GUION = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type Turnstile = {
  render: (caja: HTMLElement, opciones: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global {
  interface Window { turnstile?: Turnstile }
}

let cargando: Promise<Turnstile> | null = null;
function cargar(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  cargando ??= new Promise((ok, mal) => {
    const guion = document.createElement('script');
    guion.src = GUION;
    guion.async = true;
    guion.onload = () => (window.turnstile ? ok(window.turnstile) : mal(new Error('turnstile')));
    guion.onerror = () => { cargando = null; mal(new Error('turnstile')); };
    document.head.appendChild(guion);
  });
  return cargando;
}

/**
 * El cuadro de verificación y su token. Cada token sirve una sola vez: tras mandarlo a Supabase hay que llamar a
 * `usado()` para que Turnstile prepare otro (tarda un segundo y casi nunca pide nada a la persona).
 */
export function useVerificacion() {
  const { idioma } = useIdioma();
  const { tema } = useTema();
  const caja = useRef<HTMLDivElement>(null);
  const id = useRef<string | null>(null);
  const [token, setToken] = useState('');
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    let vivo = true;
    cargar()
      .then((turnstile) => {
        if (!vivo || !caja.current) return;
        id.current = turnstile.render(caja.current, {
          sitekey: CLAVE_DEL_SITIO,
          language: idioma,
          theme: tema === 'oscuro' ? 'dark' : 'light',
          size: 'flexible',
          callback: (nuevo: string) => { setToken(nuevo); setFallo(false); },
          'expired-callback': () => setToken(''),
          'error-callback': () => { setToken(''); setFallo(true); },
        });
      })
      .catch(() => { if (vivo) setFallo(true); });
    return () => {
      vivo = false;
      if (id.current) window.turnstile?.remove(id.current);
      id.current = null;
      setToken('');
    };
  }, [idioma, tema]);

  const usado = useCallback(() => {
    setToken('');
    if (id.current) window.turnstile?.reset(id.current);
  }, []);

  return { token, usado, fallo, cuadro: <div ref={caja} className="verificacion" /> };
}
